import { Model, ModelAttributes, ValidationError, model, modelConfig, reference } from "@mixitone/oom";
import AccountUser from "./AccountUser";
import { setAccountId } from "./concerns/SetAccountId";
import { Database } from "./database.types";

const config = modelConfig(
  {
    acceptance: {
      type: "enum",
      enumType: "none" as Database["public"]["Enums"]["club_document_acceptance"],
    },
    account_id: {
      type: "string",
    },
    audience: {
      type: "enum",
      enumType: "admins" as Database["public"]["Enums"]["club_document_audience"],
    },
    club_id: {
      type: "string",
    },
    created_at: {
      type: "datetime",
    },
    document_id: {
      type: "string",
    },
    file_storage: {
      type: "enum",
      enumType: "public_images" as Database["public"]["Enums"]["club_document_file_storage"],
    },
    is_deleted: {
      type: "boolean",
    },
    name: {
      type: "string",
    },
    series_id: {
      type: "string",
    },
    updated_at: {
      type: "datetime",
    },
    uploaded_by_id: {
      type: "string",
    },
    version: {
      type: "number",
    },
  },
  {},
  {
    uploaded_by: reference(() => AccountUser, "uploaded_by_id"),
  },
);

interface ClubDocument extends ModelAttributes<typeof config> {}

@setAccountId
@model("club_documents", config)
class ClubDocument extends Model<typeof config> {
  get path() {
    return `/${this.account_id}/documents/${this.document_id}`;
  }

  get title() {
    return `${this.name} (v${this.version})`;
  }

  /** True when members must accept some version. Mirrors the old members_must_accept flag. */
  get requiresAcceptance() {
    return this.acceptance != null && this.acceptance !== "none";
  }

  override async save() {
    if (this.acceptance && this.acceptance !== "none" && this.audience !== "members") {
      throw new ValidationError({
        acceptance: "Acceptance only applies to documents shared with members",
      });
    }
    return super.save();
  }

  static onlyLastVersion(documents: ClubDocument[]): ClubDocument[] {
    return Object.values(
      documents.reduce(
        (acc, doc) => {
          const key = doc.name!;
          const existing = acc[key];

          if (existing && (existing.version ?? 0) < (doc.version ?? 1)) {
            acc[key] = doc;
          } else if (!existing) {
            acc[key] = doc;
          }
          return acc;
        },
        {} as Record<string, ClubDocument>,
      ),
    ).toSorted((a, b) => a.name!.localeCompare(b.name!));
  }
}
export default ClubDocument;
